import React, { useEffect, useState } from 'react';
import { Card, Table, Button, Space, Tag, Alert, Popconfirm, message, Input } from 'antd';
import { ReloadOutlined, RollbackOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import axios from 'axios';
import authService from '../utils/auth';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3060/api';

const TYPE_LABEL = { standard: '出境游', daytour: '一日游', inbound: '入境游' };

export default function TourTrash() {
  const role = authService.getUserInfo()?.role;
  const isAdmin = role === 'admin';

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  const fetchList = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/tours/trash`, { headers: authService.getAuthHeaders() });
      setList(res.data.data || []);
    } catch (err) {
      message.error('获取回收站失败：' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchList(); }, []);

  const handleRestore = async (record) => {
    try {
      await axios.post(`${API_BASE}/tours/${record.id}/restore`, {}, { headers: authService.getAuthHeaders() });
      message.success(`已还原：${record.productName}`);
      fetchList();
    } catch (err) {
      message.error('还原失败：' + (err.response?.data?.error || err.message));
    }
  };

  const handleForceDelete = async (record) => {
    try {
      await axios.delete(`${API_BASE}/tours/${record.id}/force`, { headers: authService.getAuthHeaders() });
      message.success(`已永久删除：${record.productName}`);
      fetchList();
    } catch (err) {
      message.error('永久删除失败：' + (err.response?.data?.error || err.message));
    }
  };

  const filtered = list.filter(t => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (t.productName || '').toLowerCase().includes(s) ||
           (t.destination?.name || '').toLowerCase().includes(s);
  });

  const columns = [
    {
      title: '团号',
      dataIndex: 'productName',
      width: 200,
      render: v => <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{v}</span>
    },
    {
      title: '产品',
      key: 'destination',
      render: (_, r) => r.destination?.name || '-'
    },
    {
      title: '出团日期',
      dataIndex: 'departureDate',
      width: 120,
      render: v => v ? dayjs(v).format('YYYY-MM-DD') : '-'
    },
    {
      title: '类型',
      dataIndex: 'tourType',
      width: 90,
      render: v => {
        const colorMap = { standard: 'blue', daytour: 'green', inbound: 'orange' };
        return <Tag color={colorMap[v] || 'default'}>{TYPE_LABEL[v] || v}</Tag>;
      }
    },
    {
      title: '团费 (NZD)',
      dataIndex: 'adultPrice',
      width: 110,
      render: v => v ? `$${Number(v).toFixed(2)}` : '-'
    },
    {
      title: '删除时间',
      dataIndex: 'deletedAt',
      width: 160,
      render: v => v ? dayjs(v).format('YYYY-MM-DD HH:mm:ss') : '-'
    },
    {
      title: '操作',
      key: 'actions',
      fixed: 'right',
      width: 220,
      render: (_, r) => (
        <Space size={4}>
          <Popconfirm
            title="还原此团？"
            description="还原后会恢复到原状态，但已删除的关联数据（游客等）不会恢复"
            onConfirm={() => handleRestore(r)}
          >
            <Button size="small" type="primary" icon={<RollbackOutlined />}>还原</Button>
          </Popconfirm>
          {isAdmin && (
            <Popconfirm
              title="永久删除此团？"
              description="此操作不可恢复，相关数据将彻底清除"
              okType="danger"
              onConfirm={() => handleForceDelete(r)}
            >
              <Button size="small" danger icon={<DeleteOutlined />}>永久删除</Button>
            </Popconfirm>
          )}
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: 16 }}>
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 12 }}
        message="团回收站"
        description={
          <>
            此处显示已删除的团，可以「还原」或{isAdmin ? '「永久删除」' : '由管理员永久删除'}。
            <br />
            <strong>提示：</strong>还原可恢复团本身，但删除时已被级联删除的游客/酒店分配等关联记录无法找回。如需找回完整数据，需要管理员从数据库备份恢复。
          </>
        }
      />

      <Card
        title={`回收站（${list.length}）`}
        extra={
          <Space>
            <Input.Search
              placeholder="搜索团号 / 产品"
              allowClear
              style={{ width: 240 }}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Button icon={<ReloadOutlined />} onClick={fetchList}>刷新</Button>
          </Space>
        }
      >
        <Table
          rowKey="id"
          loading={loading}
          dataSource={filtered}
          columns={columns}
          size="small"
          scroll={{ x: 1100 }}
          pagination={{ pageSize: 20, showSizeChanger: true, showTotal: t => `共 ${t} 条` }}
          locale={{ emptyText: '回收站为空' }}
        />
      </Card>
    </div>
  );
}
